import { useState, useEffect, useCallback, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import LoadMapDialog from "./LoadMapDialog";
import SaveCurrentMapNameDialog from "./SaveCurrentMapNameDialog";
import { useGetMissionsQuery, useCreateMissionMutation } from "../../graphql/missions.generated";
import { useCreateActionMutation } from "app/services/missionStorageApiEnhanced";
import {
  useGetBehaviourProfilesQuery,
  useSetBehaviourMissionProfileMutation,
} from "app/services/behaviourRulesApi";
import { useARC } from "providers/ARC/ARCProvider";
import { useMapManager } from "providers/navigation/MapManagerProvider";
import { ConnectionState } from "lib/consts/ARC/connection";
import { setMapUpdatePublish } from "lib/protocols/mapLoad";
import { PREPENDED_LOAD_MAP_NOTE } from "lib/mission/dockingMissionSteps";
import {
  fetchMissionStorageMaps,
  findMapIdByStcmStem,
  stcmRelativePathForLoadMapPickerRow,
  type MissionStorageLoadMapPickerRow,
} from "lib/mission/missionStorageMaps";
import { RootState } from "store";

export type CreateMissionWizardPrefill = {
  mapStartMode: "new" | "existing" | "legacy" | "current";
  initialExistingMapDone: boolean;
};

type MapStartMode = CreateMissionWizardPrefill["mapStartMode"];

type PendingCurrentMap = {
  mapId: string;
  label: string;
};

interface CreateMissionWizardModalProps {
  visible: boolean;
  onClose: () => void;
  onComplete: (result: { missionId: string; prefill: CreateMissionWizardPrefill }) => void;
}

const normalizeMissionName = (name: string) => name.trim().toLowerCase();

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const CreateMissionWizardModal: FC<CreateMissionWizardModalProps> = ({
  visible,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation(["missions", "common"]);
  const { data: missionsData } = useGetMissionsQuery();
  const [createMission] = useCreateMissionMutation();
  const [createAction] = useCreateActionMutation();
  const [setBehaviourMissionProfile] = useSetBehaviourMissionProfileMutation();
  const { data: profilesData, isError: isProfilesError } = useGetBehaviourProfilesQuery(undefined, {
    skip: !visible,
  });
  const { selectedARC, controlSocket, connectionState, sendCommand } = useARC();
  const { clearMap, loadStcmFromRelativePath, saveMap } = useMapManager();
  const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [draftName, setDraftName] = useState("");
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLoadMap, setShowLoadMap] = useState(false);
  const [showCurrentMapName, setShowCurrentMapName] = useState(false);
  const [currentMapSaveBusy, setCurrentMapSaveBusy] = useState(false);
  const [pendingMapMode, setPendingMapMode] = useState<MapStartMode | null>(null);
  const [pendingMapSelection, setPendingMapSelection] =
    useState<MissionStorageLoadMapPickerRow | null>(null);
  const [pendingCurrentMap, setPendingCurrentMap] = useState<PendingCurrentMap | null>(null);

  const profiles = profilesData?.profiles ?? [];

  const reset = useCallback(() => {
    setStep(1);
    setDraftName("");
    setNameError("");
    setSubmitError("");
    setBusy(false);
    setShowLoadMap(false);
    setShowCurrentMapName(false);
    setCurrentMapSaveBusy(false);
    setPendingMapMode(null);
    setPendingMapSelection(null);
    setPendingCurrentMap(null);
  }, []);

  useEffect(() => {
    if (!visible) return;
    reset();
  }, [visible, reset]);

  const handleClose = () => {
    onClose();
  };

  const isDuplicateName = (candidate: string) => {
    const n = normalizeMissionName(candidate);
    return (missionsData?.missions || []).some((m) => normalizeMissionName(m.name) === n);
  };

  const disableMappingIfNeeded = () => {
    if (!controlSocket || connectionState !== ConnectionState.Connected) return;
    try {
      sendCommand(setMapUpdatePublish(false));
    } catch (e) {
      console.warn("CreateMissionWizard: failed to disable mapping", e);
    }
  };

  const resolveMapIdAfterDeviceSave = async (baseName: string): Promise<string | null> => {
    for (let attempt = 0; attempt < 15; attempt++) {
      try {
        const maps = await fetchMissionStorageMaps(arcProxyHost, arcId);
        const id = findMapIdByStcmStem(maps, baseName);
        if (id) return id;
      } catch {
        /* maps query may be unavailable; retry */
      }
      await delay(350 + attempt * 120);
    }
    return null;
  };

  const goNextFromName = () => {
    setNameError("");
    setSubmitError("");
    const trimmed = draftName.trim();
    if (!trimmed) {
      setNameError(t("wizard.nameRequired"));
      return;
    }
    if (isDuplicateName(trimmed)) {
      setNameError(t("wizard.nameExists"));
      return;
    }
    setStep(2);
  };

  const goToProfileStep = (mapMode: MapStartMode) => {
    setSubmitError("");
    setPendingMapMode(mapMode);
    setStep(3);
  };

  const handleSelectNewMap = () => {
    goToProfileStep("new");
  };

  const handleSelectLegacy = () => {
    goToProfileStep("legacy");
  };

  const handleSelectCurrentMap = () => {
    setSubmitError("");
    disableMappingIfNeeded();
    setShowCurrentMapName(true);
  };

  const handleExistingMapConfirmed = (selection: MissionStorageLoadMapPickerRow) => {
    setShowLoadMap(false);
    setPendingMapSelection(selection);
    setPendingCurrentMap(null);
    goToProfileStep("existing");
  };

  const handleCurrentMapNameConfirmed = async (normalizedBaseName: string) => {
    if (connectionState !== ConnectionState.Connected) {
      setSubmitError(t("wizard.currentMapNotConnected"));
      setShowCurrentMapName(false);
      return;
    }
    setCurrentMapSaveBusy(true);
    setSubmitError("");
    try {
      await saveMap(normalizedBaseName, "");
      const mapId = await resolveMapIdAfterDeviceSave(normalizedBaseName);
      if (!mapId) {
        setSubmitError(t("wizard.currentMapIdMissing"));
        return;
      }
      setPendingMapSelection(null);
      setPendingCurrentMap({ mapId, label: normalizedBaseName });
      setShowCurrentMapName(false);
      goToProfileStep("current");
    } catch (e) {
      const message =
        e instanceof Error ? e.message : t("wizard.currentMapSaveFailed");
      setSubmitError(message);
    } finally {
      setCurrentMapSaveBusy(false);
    }
  };

  const finishWizard = async (profileId: string | null) => {
    if (!pendingMapMode) {
      setSubmitError(t("wizard.mapRequired"));
      setStep(2);
      return;
    }

    const name = draftName.trim();
    setBusy(true);
    setSubmitError("");
    try {
      const res = await createMission({ input: { name } }).unwrap();
      const missionId = res.createMission.id;

      if (profileId) {
        await setBehaviourMissionProfile({ missionId, profileId }).unwrap();
      }

      if (pendingMapMode === "new") {
        clearMap();
      } else if (pendingMapMode === "existing") {
        if (!pendingMapSelection) {
          throw new Error("No map selected.");
        }
        const selection = pendingMapSelection;
        await createAction({
          input: {
            missionId,
            actionType: "LoadMap",
            name: `Load Map 1: ${selection.label}`,
            note: PREPENDED_LOAD_MAP_NOTE,
            args: {
              map_id: selection.mapId,
              ...(selection.variantBmpRelPath
                ? { variant_bmp_rel_path: selection.variantBmpRelPath }
                : {}),
              ...(selection.mapFileId ? { map_file_id: selection.mapFileId } : {}),
            },
            sequenceOrder: 0,
          },
        }).unwrap();
        try {
          const stcmRel = stcmRelativePathForLoadMapPickerRow(selection);
          await loadStcmFromRelativePath(stcmRel, {
            variantBmpRelativePath: selection.variantBmpRelPath,
            mapId: selection.mapId,
            mapFileId: selection.mapFileId,
          });
        } catch (e) {
          console.error("CreateMissionWizard: failed to load selected map on ARC", e);
        }
      } else if (pendingMapMode === "current") {
        if (!pendingCurrentMap) {
          throw new Error("No current map saved.");
        }
        await createAction({
          input: {
            missionId,
            actionType: "LoadMap",
            name: `Load Map 1: ${pendingCurrentMap.label}`,
            note: PREPENDED_LOAD_MAP_NOTE,
            args: { map_id: pendingCurrentMap.mapId },
            sequenceOrder: 0,
          },
        }).unwrap();
      }

      onComplete({
        missionId,
        prefill: {
          mapStartMode: pendingMapMode,
          initialExistingMapDone: true,
        },
      });
    } catch (e) {
      const message =
        e &&
        typeof e === "object" &&
        "data" in e &&
        (e as { data?: { detail?: string } }).data?.detail
          ? String((e as { data: { detail: string } }).data.detail)
          : e instanceof Error
          ? e.message
          : t("wizard.createFailed");
      setSubmitError(message);
    } finally {
      setBusy(false);
    }
  };

  const stepTitle =
    step === 1
      ? t("wizard.stepCreate")
      : step === 2
      ? t("wizard.stepMap")
      : t("wizard.stepBehaviour");

  const stepDescription =
    step === 1
      ? t("wizard.step1Description")
      : step === 2
      ? t("wizard.step2Description")
      : t("wizard.step3Description");

  return (
    <>
      <AnimatedModal
        visible={visible}
        onClose={handleClose}
        className="relative w-full max-w-lg rounded-r10 border border-black80 bg-black100 p-4 shadow-lg"
        zIndexClassName="z-[200]"
      >
        <div role="dialog" aria-labelledby="create-mission-wizard-title">
          <FunctionalButton
            icon={<CloseIcon />}
            className="absolute right-3 top-3"
            onClick={handleClose}
          />
          <h3 id="create-mission-wizard-title" className="text-h4 mb-1 pr-10">
            {stepTitle}
          </h3>
          <p className="text-h5 mb-4 text-black40">{stepDescription}</p>

          {step === 1 && (
            <>
              <label className="text-h5 mb-1 block text-black40" htmlFor="create-mission-name">
                {t("wizard.missionNameLabel")}
              </label>
              <input
                id="create-mission-name"
                type="text"
                className="text-h5 mb-2 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2 outline-none focus:border-primary2"
                placeholder={t("wizard.namePlaceholder")}
                value={draftName}
                disabled={busy}
                onChange={(e) => {
                  setDraftName(e.target.value);
                  if (nameError) setNameError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") goNextFromName();
                }}
              />
              <div className="mb-3 min-h-10">
                {nameError ? <p className="text-h5 text-warning">{nameError}</p> : null}
              </div>
              <div className="flex justify-end gap-2">
                <FunctionalButton
                  title={t("common:cancel")}
                  className="text-h5 h-9 rounded-r5 border border-black70 bg-black90 px-4"
                  onClick={handleClose}
                />
                <FunctionalButton
                  title={t("common:next")}
                  className="text-h5 hover:bg-primary1 h-9 rounded-r5 border border-primary2 bg-primary2 px-4 disabled:opacity-50"
                  disabled={busy}
                  onClick={goNextFromName}
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-3 flex flex-col gap-2">
                <button
                  type="button"
                  disabled={busy || currentMapSaveBusy}
                  className="text-h5 rounded-r5 border border-black70 bg-black70 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                  onClick={handleSelectNewMap}
                >
                  <span className="font-semibold text-white">{t("wizard.newMapTitle")}</span>
                  <span className="text-h5 mt-1 block text-black40">{t("wizard.newMapDesc")}</span>
                </button>
                <button
                  type="button"
                  disabled={busy || currentMapSaveBusy}
                  className="text-h5 rounded-r5 border border-black70 bg-black70 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                  onClick={handleSelectCurrentMap}
                >
                  <span className="font-semibold text-white">{t("wizard.currentMapTitle")}</span>
                  <span className="text-h5 mt-1 block text-black40">
                    {t("wizard.currentMapDesc")}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={busy || currentMapSaveBusy}
                  className="text-h5 rounded-r5 border border-black70 bg-black70 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                  onClick={() => setShowLoadMap(true)}
                >
                  <span className="font-semibold text-white">{t("wizard.existingMapTitle")}</span>
                  <span className="text-h5 mt-1 block text-black40">
                    {t("wizard.existingMapDesc")}
                  </span>
                </button>
                <button
                  type="button"
                  disabled={busy || currentMapSaveBusy}
                  className="text-h5 rounded-r5 border border-black70 bg-black70 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                  onClick={handleSelectLegacy}
                >
                  <span className="font-semibold text-white">{t("wizard.legacyTitle")}</span>
                  <span className="text-h5 mt-1 block text-black40">{t("wizard.legacyDesc")}</span>
                </button>
              </div>
              {submitError ? <p className="text-h5 mb-3 text-warning">{submitError}</p> : null}
              <div className="flex justify-between gap-2">
                <FunctionalButton
                  title={t("common:back")}
                  className="text-h5 h-9 rounded-r5 border border-black70 bg-black90 px-4"
                  disabled={busy || currentMapSaveBusy}
                  onClick={() => {
                    setSubmitError("");
                    setStep(1);
                  }}
                />
                <FunctionalButton
                  title={t("common:cancel")}
                  className="text-h5 h-9 rounded-r5 border border-black70 bg-black90 px-4"
                  disabled={busy || currentMapSaveBusy}
                  onClick={handleClose}
                />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              {isProfilesError && (
                <p className="text-h5 mb-3 text-warning">{t("wizard.profilesLoadError")}</p>
              )}
              <div className="mb-3 flex max-h-64 flex-col gap-2 overflow-y-auto">
                {profiles.map((profile) => (
                  <button
                    key={profile.id}
                    type="button"
                    disabled={busy}
                    className="text-h5 rounded-r5 border border-black70 bg-black70 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                    onClick={() => void finishWizard(profile.id)}
                  >
                    <span className="font-semibold text-white">{profile.name}</span>
                    <span className="text-h5 mt-1 block text-black40">
                      {t("wizard.ruleCount", { count: profile.rule_count })}
                    </span>
                  </button>
                ))}
                {profiles.length === 0 && !isProfilesError && (
                  <p className="text-h5 text-black40">{t("wizard.noProfilesHint")}</p>
                )}
                <button
                  type="button"
                  disabled={busy}
                  className="text-h5 rounded-r5 border border-dashed border-black70 bg-black90 px-4 py-3 text-left transition-colors hover:border-primary2 hover:bg-black80 disabled:opacity-50"
                  onClick={() => void finishWizard(null)}
                >
                  <span className="font-semibold text-white">{t("wizard.configureLater")}</span>
                  <span className="text-h5 mt-1 block text-black40">
                    {t("wizard.configureLaterHint")}
                  </span>
                </button>
              </div>
              {submitError ? <p className="text-h5 mb-3 text-warning">{submitError}</p> : null}
              <div className="flex justify-between gap-2">
                <FunctionalButton
                  title={t("common:back")}
                  className="text-h5 h-9 rounded-r5 border border-black70 bg-black90 px-4"
                  disabled={busy}
                  onClick={() => {
                    setSubmitError("");
                    setPendingMapMode(null);
                    setPendingMapSelection(null);
                    setPendingCurrentMap(null);
                    setStep(2);
                  }}
                />
                <FunctionalButton
                  title={t("common:cancel")}
                  className="text-h5 h-9 rounded-r5 border border-black70 bg-black90 px-4"
                  disabled={busy}
                  onClick={handleClose}
                />
              </div>
            </>
          )}
        </div>
      </AnimatedModal>

      <LoadMapDialog
        visible={showLoadMap}
        arcHost={selectedARC?.host ?? null}
        onClose={() => setShowLoadMap(false)}
        onConfirm={handleExistingMapConfirmed}
      />

      <SaveCurrentMapNameDialog
        visible={showCurrentMapName}
        busy={currentMapSaveBusy}
        onConfirm={(baseName) => void handleCurrentMapNameConfirmed(baseName)}
        onCancel={() => {
          if (currentMapSaveBusy) return;
          setShowCurrentMapName(false);
        }}
      />
    </>
  );
};

export default CreateMissionWizardModal;
